<script setup lang="ts">
import { onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { Toaster } from '@/components/ui/toast'
import MinimizedDocumentsWidget from '@/components/MinimizedDocumentsWidget.vue'
import DocumentViewerWidget from '@/components/DocumentViewerWidget.vue'
import DocumentViewerPanel from '@/components/DocumentViewerPanel.vue'
import HelpCenterWidget from '@/components/HelpCenterWidget.vue'
import HelpCenterPanel from '@/components/HelpCenterPanel.vue'
import VoiceNotesWidget from '@/components/VoiceNotesWidget.vue'
import VoiceNotesPanel from '@/components/VoiceNotesPanel.vue'
import CopilotWidget from '@/components/CopilotWidget.vue'
import CopilotPanel from '@/components/CopilotPanel.vue'
import TasksWidget from '@/components/TasksWidget.vue'
import TasksPanel from '@/components/TasksPanel.vue'

import { useDocumentViewer } from '@/composables/useDocumentViewer'

const { locale } = useI18n()
const route = useRoute()
const authStore = useAuthStore()
const { initFromStorage } = useDocumentViewer()

const isReady = computed(() => authStore.initialized)
const isPublicPage = computed(() =>
  route.path.startsWith('/public/') || route.path === '/landing'
)
const showWidgets = computed(() => authStore.user && !isPublicPage.value)
const showHelpCenter = computed(() => authStore.user && !isPublicPage.value)
const showCopilot = computed(() => {
  if (!authStore.user || isPublicPage.value) return false
  const role = authStore.userRole
  if (role === 'superadmin') return true
  if (role !== 'admin' && role !== 'operator') return false
  return authStore.isModuleEnabled('agent')
})
const showVoiceNotes = computed(() => {
  if (!authStore.user || isPublicPage.value) return false
  const role = authStore.userRole
  if (role === 'superadmin') return true
  if (role !== 'admin' && role !== 'operator') return false
  return authStore.isModuleEnabled('voiceNotes')
})
const showTasks = computed(() => {
  if (!authStore.user || isPublicPage.value) return false
  const role = authStore.userRole
  return role === 'superadmin' || role === 'admin' || role === 'operator'
})

onMounted(async () => {
  // Initialize auth state
  await authStore.initializeAuth()

  // Load saved locale
  const savedLocale = localStorage.getItem('locale')
  if (savedLocale && (savedLocale === 'es' || savedLocale === 'en')) {
    locale.value = savedLocale
  }

  // Load user's preferred locale if logged in
  if (authStore.user?.language) {
    locale.value = authStore.user.language
  }

  // Restore document viewer from localStorage if a doc was open
  initFromStorage()
})
</script>

<template>
  <RouterView v-if="isReady" />
  <div v-else class="flex h-screen w-screen items-center justify-center">
    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
  <Toaster />
  <MinimizedDocumentsWidget v-if="showWidgets" />
  <DocumentViewerWidget v-if="showWidgets" />
  <DocumentViewerPanel v-if="showWidgets" />
  <VoiceNotesWidget v-if="showVoiceNotes" />
  <VoiceNotesPanel v-if="showVoiceNotes" />
  <TasksWidget v-if="showTasks" />
  <TasksPanel v-if="showTasks" />
  <CopilotWidget v-if="showCopilot" />
  <CopilotPanel v-if="showCopilot" />
  <HelpCenterWidget v-if="showHelpCenter" />
  <HelpCenterPanel v-if="showHelpCenter" />
</template>

<style>
::-webkit-scrollbar {
  display: none;
}
#app {
  height: 100vh;
  width: 100%;
}
</style>
